import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import { doc, getDoc, getDocs, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import AdminClassesView from './AdminClassesView';
import AdminStudentsView from './AdminStudentsView';
import AdminTeachersView from './AdminTeachersView';
import AdminUsersView from './AdminUsersView';
import AdminParentsView from './AdminParentsView';
import AdminEventsView from './AdminEventsView';
import AdminGradesView from './AdminGradesView';
import AdminPerformanceView from './AdminPerformanceView';
import AdminApprovalsView from './AdminApprovalsView';
import AdminReportsView from './AdminReportsView';
import AdminStudentReportsView from './AdminStudentReportsView';
import AdminTeacherPrepView from './AdminTeacherPrepView';
import AdminGradingView from './AdminGradingView';
import AdminHonorRollView from './AdminHonorRollView';
import AdminSettingsView from './AdminSettingsView';
import AdminAnalyticsView from './AdminAnalyticsView';
import AdminStoreView from './AdminStoreView';
import AdminTimetableView from './AdminTimetableView';
import AdminProfileView from './AdminProfileView';
import AdminScanLocationsView from './AdminScanLocationsView';
import AdminOnlineClassesView from './AdminOnlineClassesView';
import AdminSubjectManagementView from './AdminSubjectManagementView';
import AdminMessagesView from './AdminMessagesView';
import AdminPromotionsView from './AdminPromotionsView';
import AdminEnrollmentView from './AdminEnrollmentView';
import AdminTranscriptsView from './AdminTranscriptsView';
import AdminRoomsView from './AdminRoomsView';
import StudentBehaviorView from './StudentBehaviorView';
import { useSchoolName } from './useSchoolName';
import './AdminDashboard.css';

function AdminDashboard() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const schoolName = useSchoolName(adminData?.schoolId);
  
  // Batch counts for admin tab badges
  const [badgeCounts, setBadgeCounts] = useState({
    approvals: 0,
    storeOrders: 0,
    newStudents: 0,
    newParents: 0,
    absentChildren: 0,
    newTeacherPreps: 0
  });
  // Live totals + what the admin has already seen — badge shows live minus baseline
  const liveCountsRef = useRef({});
  const badgeBaselineRef = useRef({});

  const adminTabs = [
    { id: 0, title: 'Analytics', icon: '📊' },
    { id: 1, title: 'Grades', icon: '📈' },
    { id: 2, title: 'Performance', icon: '📊' },
    { id: 3, title: 'Approvals', icon: '✅' },
    { id: 4, title: 'Reports', icon: '📄' },
    { id: 5, title: 'Classes', icon: '🏫' },
    { id: 6, title: 'Teachers', icon: '👨‍🏫' },
    { id: 7, title: 'Teacher Prep', icon: '📚' },
    { id: 8, title: 'Students', icon: '👥' },
    { id: 9, title: 'Parents', icon: '👨‍👩‍👧' },
    { id: 10, title: 'Events', icon: '📅' },
    { id: 11, title: 'Grading', icon: '🎓' },
    { id: 12, title: 'Honor Roll', icon: '⭐' },
    { id: 13, title: 'Users', icon: '👤' },
    { id: 14, title: 'Settings', icon: '⚙️' },
    { id: 15, title: 'Scanner', icon: '📍' },
    { id: 16, title: 'Store', icon: '🛍️' },
    { id: 17, title: 'Timetable', icon: '🗓️' },
    { id: 18, title: 'Online Classes', icon: '📹' },
    { id: 19, title: 'Subjects', icon: '📖' },
    { id: 24, title: 'Enrollment', icon: '📝' },
    { id: 25, title: 'Promotions', icon: '⬆️' },
    { id: 26, title: 'Transcripts', icon: '📜' },
    { id: 27, title: 'Rooms', icon: '🚪' },
    { id: 20, title: 'Profile', icon: '👤' },
    { id: 21, title: 'Student Reports', icon: '🚨' },
    { id: 22, title: 'Messages', icon: '💬' },
    { id: 23, title: 'Behavior', icon: '📋' }
  ];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          let userDoc = await getDoc(doc(db, 'users', user.uid));
          // Fallback: the user doc may be keyed by a UUID instead of the auth uid
          if (!userDoc.exists()) {
            try {
              let snap = await getDocs(query(collection(db, 'users'), where('id', '==', user.uid)));
              if (snap.empty) {
                snap = await getDocs(query(collection(db, 'users'), where('email', '==', user.email)));
              }
              if (!snap.empty) userDoc = snap.docs[0];
            } catch (e) { /* ignore */ }
          }
          const userData = userDoc.exists() ? userDoc.data() : {};
          let sid = userData.schoolId || null;
          // Fallback: the admin profile doc may hold the school link
          if (!sid) {
            try {
              const adminDoc = await getDoc(doc(db, 'admins', user.uid));
              if (adminDoc.exists()) sid = adminDoc.data().schoolId || null;
            } catch (e) { /* ignore */ }
          }
          // Canonicalize casing: iOS stores school ids as uppercase UUIDs, web may hold lowercase
          if (sid) {
            try {
              const schoolDoc = await getDoc(doc(db, 'schools', sid));
              if (!schoolDoc.exists()) {
                const allSchools = await getDocs(collection(db, 'schools'));
                const match = allSchools.docs.find(d => d.id.toLowerCase() === String(sid).toLowerCase());
                if (match) sid = match.id;
              }
            } catch (e) { /* ignore */ }
          }
          console.log('🔥 AdminDashboard: Admin data loaded', { uid: user.uid, schoolId: sid, role: userData.role });
          setAdminData({
            uid: user.uid,
            uuid: userData.uuid || null,
            email: user.email,
            displayName: userData.username || user.displayName || user.email.split('@')[0],
            role: userData.role?.toLowerCase() || 'admin',
            schoolId: sid
          });
        } catch (error) {
          console.error('Error loading admin data:', error);
          setAdminData({
            uid: user.uid,
            uuid: null,
            email: user.email,
            displayName: user.displayName || user.email.split('@')[0],
            role: 'admin',
            schoolId: null
          });
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Live badge counts — onSnapshot listeners so counts update the instant data changes
  useEffect(() => {
    if (!adminData) return;

    const sid = String(adminData.schoolId || '').toLowerCase();
    const inScope = (data) => !sid || String(data.schoolId || '').toLowerCase() === sid;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const counts = { approvals: 0, storeOrders: 0, newParents: 0, todayEvents: 0 };
    const perClass = {};   // classId -> { students, absent, preps }
    const classUnsubs = {}; // classId -> [unsubscribes]
    const topUnsubs = [];

    const recompute = () => {
      let newStudents = 0, absentChildren = 0, newTeacherPreps = 0;
      Object.values(perClass).forEach(c => {
        newStudents += c.students;
        absentChildren += c.absent;
        newTeacherPreps += c.preps;
      });
      const live = { ...counts, newStudents, absentChildren, newTeacherPreps };
      liveCountsRef.current = live;
      // Show only items newer than what the admin last saw (baseline set on tab tap)
      const visible = {};
      Object.keys(live).forEach(k => {
        visible[k] = Math.max(0, live[k] - (badgeBaselineRef.current[k] || 0));
      });
      setBadgeCounts(visible);
    };

    // Pending grade-change approvals (top-level mirror collection)
    topUnsubs.push(onSnapshot(collection(db, 'gradeChangeRequests'), snap => {
      counts.approvals = snap.docs.filter(d => {
        const data = d.data();
        return inScope(data) && String(data.status || 'pending').toLowerCase() === 'pending';
      }).length;
      recompute();
    }, err => console.error('🔥 Badge listener (gradeChangeRequests):', err)));

    // Pending store orders
    topUnsubs.push(onSnapshot(collection(db, 'storeOrders'), snap => {
      counts.storeOrders = snap.docs.filter(d => {
        const data = d.data();
        return inScope(data) && String(data.status || 'pending').toLowerCase() === 'pending';
      }).length;
      recompute();
    }, err => console.error('🔥 Badge listener (storeOrders):', err)));

    // New parents (last 7 days)
    topUnsubs.push(onSnapshot(collection(db, 'parents'), snap => {
      counts.newParents = snap.docs.filter(d => {
        const data = d.data();
        if (!inScope(data)) return false;
        return data.createdAt && new Date(data.createdAt) >= sevenDaysAgo;
      }).length;
      recompute();
    }, err => console.error('🔥 Badge listener (parents):', err)));

    // Events happening today
    topUnsubs.push(onSnapshot(collection(db, 'events'), snap => {
      counts.todayEvents = snap.docs.filter(d => {
        const data = d.data();
        if (!inScope(data) || !data.date) return false;
        const eDate = new Date(data.date);
        return eDate >= today && eDate < tomorrow;
      }).length;
      recompute();
    }, err => console.error('🔥 Badge listener (events):', err)));

    // Classes — then per-class listeners for students / attendance / teacher preps
    topUnsubs.push(onSnapshot(collection(db, 'classes'), snap => {
      const liveClassIds = new Set();
      snap.docs.forEach(d => { if (inScope(d.data())) liveClassIds.add(d.id); });

      // Drop listeners for classes that left scope or were deleted
      Object.keys(classUnsubs).forEach(cid => {
        if (!liveClassIds.has(cid)) {
          classUnsubs[cid].forEach(u => u());
          delete classUnsubs[cid];
          delete perClass[cid];
        }
      });

      // Attach listeners for newly seen classes
      liveClassIds.forEach(cid => {
        if (classUnsubs[cid]) return;
        perClass[cid] = { students: 0, absent: 0, preps: 0 };
        classUnsubs[cid] = [
          onSnapshot(collection(db, 'classes', cid, 'students'), s => {
            perClass[cid].students = s.docs.filter(d =>
              d.data().createdAt && new Date(d.data().createdAt) >= sevenDaysAgo
            ).length;
            recompute();
          }),
          onSnapshot(collection(db, 'classes', cid, 'attendanceRecords'), s => {
            perClass[cid].absent = s.docs.filter(d => {
              const data = d.data();
              if (!data.date) return false;
              const dDate = new Date(data.date);
              return dDate >= today && dDate < tomorrow && String(data.status || '').toLowerCase() === 'absent';
            }).length;
            recompute();
          }),
          onSnapshot(collection(db, 'classes', cid, 'teacherPreps'), s => {
            perClass[cid].preps = s.docs.filter(d =>
              d.data().createdAt && new Date(d.data().createdAt) >= sevenDaysAgo
            ).length;
            recompute();
          })
        ];
      });

      recompute();
    }, err => console.error('🔥 Badge listener (classes):', err)));

    return () => {
      topUnsubs.forEach(u => u());
      Object.values(classUnsubs).flat().forEach(u => u());
    };
  }, [adminData]);

  const getBadgeCount = (tabId) => {
    switch (tabId) {
      case 3: return badgeCounts.approvals; // Approvals
      case 7: return badgeCounts.newTeacherPreps; // Teacher Prep
      case 8: return badgeCounts.newStudents; // Students
      case 9: return badgeCounts.newParents; // Parents
      case 10: return badgeCounts.todayEvents; // Events
      case 16: return badgeCounts.storeOrders; // Store
      default: return 0;
    }
  };

  // Clear only the badge for the tab being opened — remember the current live
  // count as the baseline so genuinely new items bump the badge again
  const clearBadgeForTab = (tabId) => {
    const key = {
      3: 'approvals',
      7: 'newTeacherPreps',
      8: 'newStudents',
      9: 'newParents',
      10: 'todayEvents',
      16: 'storeOrders'
    }[tabId];
    if (key) {
      badgeBaselineRef.current = {
        ...badgeBaselineRef.current,
        [key]: liveCountsRef.current[key] || 0
      };
      setBadgeCounts(prev => ({ ...prev, [key]: 0 }));
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const noSchool = !!adminData && !adminData.schoolId;
  const noSchoolNotice = (
    <div className="empty-state" style={{ padding: 40, textAlign: 'center' }}>
      <h3>No school linked</h3>
      <p>Your admin account is not linked to a school, so no data can be shown. Please contact the Super Admin.</p>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div className="header-brand">
          <img src="/logo512.png" alt="KN Education Portal logo" className="header-logo" />
          <div>
            <h1>KN Education Portal</h1>
            {schoolName && <span className="header-school">{schoolName}</span>}
          </div>
        </div>
        <div className="header-actions">
          <span className="admin-name">{adminData?.displayName || 'Admin'}</span>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-layout">
        <nav className="sidebar">
          {adminTabs.map((tab) => {
            const badgeCount = getBadgeCount(tab.id);
            return (
              <button
                key={tab.id}
                className={`nav-button ${selectedTab === tab.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedTab(tab.id);
                  clearBadgeForTab(tab.id);
                }}
              >
                <span className="nav-icon">{tab.icon}</span>
                {badgeCount > 0 && (
                  <span className="nav-badge">{badgeCount}</span>
                )}
                <span className="nav-label">{tab.title}</span>
              </button>
            );
          })}
        </nav>

        <main className="main-content">
          {noSchool ? noSchoolNotice : (<>
          {selectedTab === 0 && <AdminAnalyticsView adminData={adminData} />}
          {selectedTab === 1 && <AdminGradesTab adminData={adminData} />}
          {selectedTab === 2 && <AdminPerformanceTab adminData={adminData} />}
          {selectedTab === 3 && <AdminApprovalsTab adminData={adminData} />}
          {selectedTab === 4 && <AdminReportsTab adminData={adminData} />}
          {selectedTab === 5 && <AdminClassesTab adminData={adminData} />}
          {selectedTab === 6 && <AdminTeachersTab adminData={adminData} />}
          {selectedTab === 7 && <AdminTeacherPrepTab adminData={adminData} />}
          {selectedTab === 8 && <AdminStudentsTab adminData={adminData} />}
          {selectedTab === 9 && <AdminParentsTab adminData={adminData} />}
          {selectedTab === 10 && <AdminEventsTab adminData={adminData} />}
          {selectedTab === 11 && <AdminGradingTab adminData={adminData} />}
          {selectedTab === 12 && <AdminHonorRollTab adminData={adminData} />}
          {selectedTab === 13 && <AdminUsersTab adminData={adminData} />}
          {selectedTab === 14 && <AdminSettingsTab adminData={adminData} />}
          {selectedTab === 15 && <AdminScanLocationsView adminData={adminData} />}
          {selectedTab === 16 && <AdminStoreView adminData={adminData} />}
          {selectedTab === 17 && <AdminTimetableTab adminData={adminData} />}
          {selectedTab === 18 && <AdminOnlineClassesView schoolId={adminData?.schoolId} />}
          {selectedTab === 19 && adminData && <AdminSubjectManagementView adminData={adminData} />}
          {selectedTab === 24 && <AdminEnrollmentView adminData={adminData} />}
          {selectedTab === 25 && <AdminPromotionsView adminData={adminData} />}
          {selectedTab === 26 && <AdminTranscriptsView adminData={adminData} />}
          {selectedTab === 27 && <AdminRoomsView adminData={adminData} />}
          {selectedTab === 20 && <AdminProfileTab adminData={adminData} />}
          {selectedTab === 21 && <AdminStudentReportsTab adminData={adminData} />}
          {selectedTab === 22 && <AdminMessagesView adminData={adminData} />}
          {selectedTab === 23 && adminData && <StudentBehaviorView user={adminData} mode="admin" />}
          </>)}
        </main>
      </div>

      {/* Mobile-only content area — outside dashboard-layout to avoid flex height collapse */}
      <main className="mobile-main-content">
        {noSchool ? noSchoolNotice : (<>
        {selectedTab === 0 && <AdminAnalyticsView adminData={adminData} />}
        {selectedTab === 1 && <AdminGradesTab adminData={adminData} />}
        {selectedTab === 2 && <AdminPerformanceTab adminData={adminData} />}
        {selectedTab === 3 && <AdminApprovalsTab adminData={adminData} />}
        {selectedTab === 4 && <AdminReportsTab adminData={adminData} />}
        {selectedTab === 5 && <AdminClassesTab adminData={adminData} />}
        {selectedTab === 6 && <AdminTeachersTab adminData={adminData} />}
        {selectedTab === 7 && <AdminTeacherPrepTab adminData={adminData} />}
        {selectedTab === 8 && <AdminStudentsTab adminData={adminData} />}
        {selectedTab === 9 && <AdminParentsTab adminData={adminData} />}
        {selectedTab === 10 && <AdminEventsTab adminData={adminData} />}
        {selectedTab === 11 && <AdminGradingTab adminData={adminData} />}
        {selectedTab === 12 && <AdminHonorRollTab adminData={adminData} />}
        {selectedTab === 13 && <AdminUsersTab adminData={adminData} />}
        {selectedTab === 14 && <AdminSettingsTab adminData={adminData} />}
        {selectedTab === 15 && <AdminScanLocationsView adminData={adminData} />}
        {selectedTab === 16 && <AdminStoreView adminData={adminData} />}
        {selectedTab === 17 && <AdminTimetableTab adminData={adminData} />}
        {selectedTab === 18 && <AdminOnlineClassesView schoolId={adminData?.schoolId} />}
        {selectedTab === 19 && adminData && <AdminSubjectManagementView adminData={adminData} />}
        {selectedTab === 24 && <AdminEnrollmentView adminData={adminData} />}
        {selectedTab === 25 && <AdminPromotionsView adminData={adminData} />}
        {selectedTab === 26 && <AdminTranscriptsView adminData={adminData} />}
        {selectedTab === 27 && <AdminRoomsView adminData={adminData} />}
        {selectedTab === 20 && <AdminProfileTab adminData={adminData} />}
        {selectedTab === 21 && <AdminStudentReportsTab adminData={adminData} />}
        {selectedTab === 22 && <AdminMessagesView adminData={adminData} />}
        {selectedTab === 23 && adminData && <StudentBehaviorView user={adminData} mode="admin" />}
        </>)}
      </main>

      {/* Mobile Bottom Nav - scrollable for all tabs */}
      <nav className="mobile-bottom-nav">
        {adminTabs.map((tab) => (
          <button
            key={tab.id}
            className={`mobile-nav-btn ${selectedTab === tab.id ? 'active' : ''}`}
            onClick={() => { setSelectedTab(tab.id); clearBadgeForTab(tab.id); }}
          >
            <span className="mobile-nav-icon">{tab.icon}</span>
            <span className="mobile-nav-label">{tab.title}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function AdminClassesTab({ adminData }) {
  return <AdminClassesView adminData={adminData} />;
}

function AdminTeachersTab({ adminData }) {
  return <AdminTeachersView adminData={adminData} />;
}

function AdminStudentsTab({ adminData }) {
  return <AdminStudentsView adminData={adminData} />;
}

function AdminUsersTab({ adminData }) {
  return <AdminUsersView adminData={adminData} />;
}

function AdminSettingsTab({ adminData }) {
  return <AdminSettingsView adminData={adminData} />;
}

function AdminGradesTab({ adminData }) {
  return <AdminGradesView adminData={adminData} />;
}

function AdminPerformanceTab({ adminData }) {
  return <AdminPerformanceView adminData={adminData} />;
}

function AdminApprovalsTab({ adminData }) {
  return <AdminApprovalsView adminData={adminData} />;
}

function AdminReportsTab({ adminData }) {
  return <AdminReportsView adminData={adminData} />;
}

function AdminTeacherPrepTab({ adminData }) {
  return <AdminTeacherPrepView adminData={adminData} />;
}

function AdminParentsTab({ adminData }) {
  return <AdminParentsView adminData={adminData} />;
}

function AdminEventsTab({ adminData }) {
  return <AdminEventsView adminData={adminData} />;
}

function AdminGradingTab({ adminData }) {
  return <AdminGradingView adminData={adminData} />;
}

function AdminHonorRollTab({ adminData }) {
  return <AdminHonorRollView adminData={adminData} />;
}

function AdminTimetableTab({ adminData }) {
  return <AdminTimetableView schoolId={adminData?.schoolId} />;
}

function AdminProfileTab({ adminData }) {
  return <AdminProfileView adminData={adminData} />;
}

function AdminStudentReportsTab({ adminData }) {
  return <AdminStudentReportsView adminData={adminData} />;
}

export default AdminDashboard;
